import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Activity, Loader2 } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 60_000; // 1 minute

/**
 * CronHealthPill — green/red status pill on the Admin Overview page
 * that auto-pings GET /api/admin/scheduler/jobs every minute and shows
 * which background crons are healthy. Click to see the full job list
 * with next-run timestamps.
 *
 * Health rules:
 *   - RED  → endpoint failed, scheduler reported `running: false`,
 *            OR any registered job has no `next_run_time`.
 *   - AMBER → endpoint returned but zero jobs registered (suspicious).
 *   - GREEN → all registered jobs have a future next_run_time.
 */
export default function CronHealthPill() {
  const [state, setState] = useState({ status: 'loading', jobs: [], error: null });

  const check = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/scheduler/jobs`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000,
      });
      const { running, jobs = [] } = res.data || {};

      if (!running) {
        setState({ status: 'red', jobs, error: 'scheduler not running' });
        return;
      }
      if (jobs.length === 0) {
        setState({ status: 'amber', jobs, error: 'no jobs registered' });
        return;
      }
      const stalled = jobs.filter((j) => !j.next_run_time);
      if (stalled.length > 0) {
        setState({
          status: 'red',
          jobs,
          error: `${stalled.length} job(s) have no next run`,
        });
        return;
      }
      setState({ status: 'green', jobs, error: null });
    } catch (err) {
      setState({
        status: 'red',
        jobs: [],
        error: err.response?.data?.detail || err.message || 'request failed',
      });
    }
  }, []);

  useEffect(() => {
    check();
    const id = setInterval(check, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [check]);

  // Visual style per status
  const palette = {
    loading: { color: '#A3A3A3', bg: '#1a1a1a', border: '#333', label: 'Checking…' },
    green: { color: '#00FF94', bg: '#001A0F', border: '#00FF9444', label: 'Crons healthy' },
    amber: { color: '#00F0FF', bg: '#002830', border: '#00F0FF44', label: 'No jobs' },
    red: { color: '#FF4D4F', bg: '#1F0A0A', border: '#FF4D4F44', label: 'Cron alert' },
  }[state.status];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 transition-colors hover:opacity-90"
          style={{
            color: palette.color,
            backgroundColor: palette.bg,
            border: `1px solid ${palette.border}`,
          }}
          data-testid="cron-health-pill"
          aria-label="Background cron job health"
        >
          {state.status === 'loading' ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{
                backgroundColor: palette.color,
                boxShadow: state.status === 'green'
                  ? `0 0 6px ${palette.color}`
                  : 'none',
              }}
            />
          )}
          <Activity className="w-3.5 h-3.5" />
          {palette.label}
          {state.jobs.length > 0 && state.status !== 'loading' && (
            <span className="text-neutral-400 font-normal">· {state.jobs.length}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 bg-neutral-950 border-white/10 text-white p-0"
        data-testid="cron-health-popover"
      >
        <div className="p-4 border-b border-white/5">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-400">
            Background cron health
          </p>
          <p className="text-sm mt-1" style={{ color: palette.color }}>
            {state.error ? state.error : palette.label}
          </p>
        </div>
        <div className="p-2 max-h-72 overflow-y-auto">
          {state.jobs.length === 0 && state.status !== 'loading' && (
            <p className="text-xs text-neutral-500 p-3 text-center">
              No jobs registered.
            </p>
          )}
          {state.jobs.map((job) => (
            <div
              key={job.id}
              className="px-3 py-2 rounded-md hover:bg-white/5"
              data-testid={`cron-job-${job.id}`}
            >
              <p className="text-xs font-bold text-white truncate">{job.id}</p>
              <p className="text-[10px] text-neutral-400 truncate">
                {job.next_run_time
                  ? `Next: ${new Date(job.next_run_time).toLocaleString()}`
                  : 'No next run scheduled'}
              </p>
            </div>
          ))}
        </div>
        <div className="px-4 py-2 border-t border-white/5 text-[10px] text-neutral-500 text-center">
          Auto-refreshing every 60s
        </div>
      </PopoverContent>
    </Popover>
  );
}
